import type {GenericObject} from 'src/types/sense';
import type {ModuleProperties, CanvasNodeTypes} from 'src/types/automation';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {
  AutomationWorkflowApi,
  CanvasGraphNode,
  CompositeAutomationNodeApi,
  NextNodeApi,
  Edge,
  Group,
  Swimlane,
  SwimlaneRecurNodeMap,
  SwimlaneBatchingNodeMap,
  WorkflowStatus,
  CompositeAutomationNode,
  FrequencyLimit,
  DeleteNodeUsageWithId,
  AutomationWorkflowEditStatus,
} from 'src/types/automation-workflow';
import type {ReceivedFilters} from 'src/types/filter';

import type {Group as WorkflowGroup} from 'src/action-creators/groups';
import type {AutomationWorkflowState} from 'src/reducers/automation-workflow';
import type {Brand} from 'src/types/brands';
import type {PropsWithIconURL} from 'src/components/automation/common/panel-options';
import type {UrlGenerator} from 'src/utils/automation';
import * as React from 'react';
import {useSelector} from 'react-redux';
import {getBrandDisplayName} from 'src/utils/settings';
import {
  checkIfValidCanvasNodeType,
  getCompleteName,
} from 'src/utils/automation';
import {
  selectSwimlaneNode,
  selectSwimlane,
} from 'src/selectors/automation-workflow';
import isEmpty from 'lodash/isEmpty';
import {formatPlural} from 'src/utils/intl';


export const HF_EXCEPTION_LABELS = {
  used_in_condition: 'Used in conditions',
  used_in_body: 'Used in body',
  used_in_settings: 'Used in settings',
} as const;
export const getWorkflowStatusToText = (
  t: (arg1: string, arg2: string) => string,
): Partial<Record<WorkflowStatus, string>> => {
  return {
    ACTIVE: t('Active', 'Active'),
    INACTIVE: t('Inactive', 'Inactive'),
    PAUSE: t('Inactive', 'Inactive'), //paused status is not shown on ui
    ARCHIVE: t('Archived', 'Archived'),
    DRAFT: t('Draft', 'Draft'),
    PUBLISHED: t('Published', 'Published'),
  };
};
export const AUTOMATION_WORKFLOW_NAME_MAX_LENGTH = 80;
export const AUTOMATION_NODE_NAME_MAX_LENGTH = 80;
export const TEMP_NODE_IDENTIFIER = 'temp__temp:temp';

export const TEMP_END_NODE_IDENTIFIER = 'temp__end:null';

export const DEFAULT_BEEFREE_LOGO_URL =
  'https://s3.us-west-2.amazonaws.com/media.sense/email/logo/default_company_logo.png';

export const FREQUENCY_LIMIT = {
  NO_RESTRICTION: 'no-restrict',
  RESTRICTION: 'restrict',
  RESTRICT_REPEAT: 'restrict-repeat',
} as const;

export const FREQUENCY_DURATION_OPTIONS = {
  '1_DAY': {label: 'Once a day', duration: {type: 'DAY', value: 1, count: 1}},
  '2_DAY': {label: 'Twice a day', duration: {type: 'DAY', value: 1, count: 2}},
  '3_DAY': {label: 'Thrice a day', duration: {type: 'DAY', value: 1, count: 3}},
  '4_DAY': {
    label: 'Four times a day',
    duration: {type: 'DAY', value: 1, count: 4},
  },
  '5_DAY': {
    label: 'Five times a day',
    duration: {type: 'DAY', value: 1, count: 5},
  },
  '1_WEEK': {label: 'One week', duration: {type: 'WEEK', value: 1, count: 1}},
  '2_WEEK': {label: 'Two weeks', duration: {type: 'WEEK', value: 2, count: 1}},
  '30_DAYS': {label: '30 days', duration: {type: 'DAY', value: 30, count: 1}},
  '60_DAYS': {label: '60 days', duration: {type: 'DAY', value: 60, count: 1}},
  '90_DAYS': {label: '90 days', duration: {type: 'DAY', value: 90, count: 1}},
  '180_DAYS': {
    label: '180 days',
    duration: {type: 'DAY', value: 180, count: 1},
  },
  '365_DAYS': {
    label: '365 days',
    duration: {type: 'DAY', value: 365, count: 1},
  },
} as const;

export const ACTIVE_EDITABLE_NODE_TYPES = [
  'email',
  'sms',
  'whatsapp',
  'voicebot',
  'voicebot-v2',
  'sms-chatbot',
  'create-ats-record',
  'custom-note-writeback',
  'update-record',
  'candidate-evaluation',
  'candidate-matching',
] as const;

export const getFrequencyDurationOptions = (
  t: (arg1: string, arg2: string) => string,
): {
  [key: string]: {
    label: string;
    duration: {
      type: string;
      value: number;
      count: number;
    };
  };
} => ({
  '1_DAY': {
    label: t('Once_a_day', 'Once a day'),
    duration: {type: 'DAY', value: 1, count: 1},
  },
  '2_DAY': {
    label: t('Twice_a_day', 'Twice a day'),
    duration: {type: 'DAY', value: 1, count: 2},
  },
  '3_DAY': {
    label: t('Thrice_a_day', 'Thrice a day'),
    duration: {type: 'DAY', value: 1, count: 3},
  },
  '4_DAY': {
    label: t('Four_times_a_day', 'Four times a day'),
    duration: {type: 'DAY', value: 1, count: 4},
  },
  '5_DAY': {
    label: t('Five_times_a_day', 'Five times a day'),
    duration: {type: 'DAY', value: 1, count: 5},
  },
  '1_WEEK': {
    label: t('One_week', 'One week'),
    duration: {type: 'WEEK', value: 1, count: 1},
  },
  '2_WEEK': {
    label: t('Two_weeks', 'Two weeks'),
    duration: {type: 'WEEK', value: 2, count: 1},
  },
  '30_DAYS': {
    label: t('30_days', '30 days'),
    duration: {type: 'DAY', value: 30, count: 1},
  },
  '60_DAYS': {
    label: t('60_days', '60 days'),
    duration: {type: 'DAY', value: 60, count: 1},
  },
  '90_DAYS': {
    label: t('90_days', '90 days'),
    duration: {type: 'DAY', value: 90, count: 1},
  },
  '180_DAYS': {
    label: t('180_days', '180 days'),
    duration: {type: 'DAY', value: 180, count: 1},
  },
  '365_DAYS': {
    label: t('365_days', '365 days'),
    duration: {type: 'DAY', value: 365, count: 1},
  },
});

export const createSwimlaneNode = ({
  next_nodes,
  composite_id,
  composite_node_type,
}: {
  next_nodes: Array<NextNodeApi>;
  composite_id: string;
  composite_node_type: CanvasNodeTypes;
}): CompositeAutomationNodeApi => {
  if (!checkIfValidCanvasNodeType(composite_node_type)) {
    throw new Error(`Invalid node type: ${composite_node_type}`);
  }

  const body = {
    body: null,
    composite_id,
    composite_node_type,
    display_name: '',
    internal_node_id: '',
    node_name: '',
    swimlane_id: -1,
    workflow_id: -1,
  } as const;
  return {
    id: composite_id,
    anchor_entity_type: '',
    trigger_entity_type: '',
    //NOTE:(diwakersurya) typing this is hard because each of the nodes have
    // a different body. Now this function in only used on UI to build placeholder
    // temporary nodes before they are saved in the backend. typing this would would
    // mean that each onboarded node make change here which we dont want. so its better
    // to suppress the error. Ideally this should not cause any harm since all the field
    // values are placeholder values and dont participate in any logic. Temp node logic
    // is only based on composite_node_type and temp composite_id.
    // @ts-expect-error - TS2322 - Type '{ readonly body: null; readonly composite_id: string; readonly composite_node_type: CanvasNodeTypes; readonly display_name: ""; readonly internal_node_id: ""; readonly node_name: ""; readonly swimlane_id: -1; readonly workflow_id: -1; }' is not assignable to type 'CompositeNodeBodyApi'.
    body,
    next_nodes,
  };
};

export const createTransition = (display_name: string): NextNodeApi => {
  return {
    condition: {blocks: []},
    next_node: TEMP_END_NODE_IDENTIFIER,
    transition_id: null,
    delete_node_usage: null,
    display_name,
  };
};

export const createSplitTransition = (
  nextNodeId: string,
  display_name: string,
): NextNodeApi => {
  return {
    condition: {blocks: []},
    next_node: nextNodeId,
    transition_id: null,
    display_name,
    delete_node_usage: null,
  };
};

export const DUMMY_SWIMLANE: Swimlane = {
  id: '1',
  initial_node: 'trigger__trigger:null',
  nodes: {
    'trigger__trigger:null': createSwimlaneNode({
      composite_id: 'trigger__trigger:null',
      composite_node_type: 'trigger',
      next_nodes: [
        {
          condition: {
            blocks: [
              {
                rules: [],
              },
            ],
          },
          next_node: 'end__end:null',
          display_name: '',
          transition_id: null,
        },
      ],
    }),
    'end__end:null': createSwimlaneNode({
      composite_id: 'end__end:null',
      composite_node_type: 'end',
      next_nodes: [],
    }),
  },
};

/** a function to convert journey provided by backend to frontend store schema */
export const parseAutomationWorkflow = (
  automationWorkflow: AutomationWorkflowApi,
): AutomationWorkflowState => {
  return {
    id: automationWorkflow.id,
    name: automationWorkflow.name,
    entity_type: automationWorkflow.entity_type,
    standard_entity_type: automationWorkflow.standard_entity_type,
    starred: automationWorkflow.starred,
    time_updated: automationWorkflow.time_updated,
    status: automationWorkflow.status,
    swimlanes: automationWorkflow.swimlanes,
    filters: automationWorkflow.filters,
    workflow_category: automationWorkflow.workflow_category,
    created_by_agent_name: automationWorkflow.created_by_agent_details?.name,
    security_group: automationWorkflow.security_group_details,
    time_created: automationWorkflow.time_created,
    brand_id: automationWorkflow.brand_id,
    shared_security_groups: automationWorkflow.shared_security_groups,
    frequencyLimit: getFrequencyLimit(automationWorkflow.frequency_limit),
    frequencyType: getFrequencyType(automationWorkflow.frequency_limit),
    blackout_window_id: automationWorkflow.blackout_window_id,
    disable_preview: automationWorkflow.disable_preview,
    locked: automationWorkflow.locked,
  };
};

// const isNodeComposite = (swimlane, node) =>{

//   node.next_nodes.some(
//     (nextNode) => swimlane.nodes[nextNode.next_node].metadata.hidden,
//   ) ?? false;
// }

// const isHiddenBranchAssociatedWithTargetNode = (swimlane, node) => {
//   if (!node || !node.next_nodes || node.next_nodes.length === 0) {
//     return false;
//   }
//   const targetNode = node.next_nodes[0].next_node;
//   if (
//     targetNode &&
//     isNodeComposite(swimlane, node) &&
//     swimlane.nodes[targetNode].next_nodes.length > 1
//   ) {
//     return true;
//   }
//   return false;
// };

// const isHiddenFilterCompositeNode = (swimlane, node) => {
//   if (isNodeComposite(swimlane, node) && node.id.startsWith('branching')) {
//     return true;
//   }
//   return false;
// };

export const swimlanesToGraph = (
  swimlanes: Array<Swimlane>,
): {
  nodes: Array<CanvasGraphNode>;
  edges: Array<Edge>;
  groups: Array<Group>;
  swimlaneRecurNodeMap: SwimlaneRecurNodeMap;
  swimlaneBatchingNodeMap: SwimlaneBatchingNodeMap;
} => {
  // @ts-expect-error - TS2739 - Type 'Swimlane' is missing the following properties from type '{ nodes: CanvasGraphNode[]; edges: Edge[]; groups: Group[]; swimlaneRecurNodeMap: SwimlaneRecurNodeMap; }': edges, groups, swimlaneRecurNodeMap
  return swimlanes.reduce(
    // @ts-expect-error - TS2769 - No overload matches this call.
    (acc, swimlane) => {
      const {
        nodes,
        edges,
        groups,
        swimlaneRecurNodeMap,
        swimlaneBatchingNodeMap,
      } = swimlane.initial_node
        ? traverseSwimlane(swimlane)
        : {
            nodes: [],
            edges: [],
            groups: [],
            swimlaneRecurNodeMap: new Map(),
            swimlaneBatchingNodeMap: new Map(),
          };

      return {
        nodes: [...acc.nodes, ...nodes],
        edges: [...acc.edges, ...edges],
        groups: [...acc.groups, ...groups],
        swimlaneRecurNodeMap: new Map([
          ...acc.swimlaneRecurNodeMap,
          ...swimlaneRecurNodeMap,
        ]),
        swimlaneBatchingNodeMap: new Map([
          ...acc.swimlaneBatchingNodeMap,
          ...swimlaneBatchingNodeMap,
        ]),
      };
    },
    {
      nodes: [],
      edges: [],
      groups: [],
      swimlaneRecurNodeMap: new Map(),
      swimlaneBatchingNodeMap: new Map(),
    },
  );
};

const traverseSwimlane = (
  swimlane: Swimlane,
): {
  nodes: Array<CanvasGraphNode>;
  edges: Array<Edge>;
  groups: Array<Group>;
  swimlaneRecurNodeMap: SwimlaneRecurNodeMap;
  swimlaneBatchingNodeMap: SwimlaneBatchingNodeMap;
} => {
  const {initial_node} = swimlane;
  const {nodes, edges, groups, swimlaneRecurNodeSet, swimlaneBatchingNodeSet} =
    traverseNode(swimlane, initial_node, true, 0);
  return {
    nodes,
    edges,
    groups,
    swimlaneRecurNodeMap: new Map([[swimlane.id, swimlaneRecurNodeSet]]),
    swimlaneBatchingNodeMap: new Map([[swimlane.id, swimlaneBatchingNodeSet]]),
  };
};
const traverseNode = (
  swimlane: Swimlane,
  nodeId: string,
  isRoot: boolean,
  scheduleSeenCount: number,
): {
  nodes: Array<CanvasGraphNode>;
  edges: Array<Edge>;
  groups: Array<Group>;
  swimlaneRecurNodeSet: Set<string>;
  swimlaneBatchingNodeSet: Set<string>;
} => {
  const {id} = swimlane;
  // @ts-expect-error - TS7034 - Variable 'nodes' implicitly has type 'any[]' in some locations where its type cannot be determined.
  let nodes = [];
  let edges:
    | Array<Edge>
    | Array<
        | Edge
        | {
            actionable: boolean;
            from: string;
            id: string;
            to: string;
          }
      > = [];
  let groups: Array<Group> = [];
  let swimlaneRecurNodeSet = new Set<string>();
  let swimlaneBatchingNodeSet = new Set<string>();

  if (nodeId) {
    const node = swimlane.nodes[nodeId];
    // @ts-expect-error - TS2339 - Property 'recurring_rule' does not exist on type 'EmailApi | SmsApi | FieldWritebackAPI | CustomNoteWritebackAPI | CandidateMatchingAPI | ... 6 more ... | TriggerBodyApi'.
    const hasRecurrence = !!node.body.body?.recurring_rule;
    if (hasRecurrence) {
      swimlaneRecurNodeSet.add(nodeId);
    }
    // @ts-expect-error - TS2339 - Property 'batching_rule' does not exist on type 'EmailApi | SmsApi | FieldWritebackAPI | CustomNoteWritebackAPI | CandidateMatchingAPI | ... 6 more ... | TriggerBodyApi'.
    const hasBatching = !!node.body.body?.batching_rule;
    if (hasBatching) {
      swimlaneBatchingNodeSet.add(nodeId);
    }
    const nextNodes = node.next_nodes ?? [];

    /** if schedule has already occured once in any node, the next node shows different screen for
     * scheduling. This flag gives information to the next node that schedule has already occured once
     * in the swimlane so that it can show different screen for scheduling
     */
    const newScheduleSeenCount = nodeId.includes('__schedule')
      ? scheduleSeenCount + 1
      : scheduleSeenCount;

    // if (!relatedNodes.has(nodeId) && !hiddenNodesSet.has(nodeId)) {
    nodes = [
      // @ts-expect-error - TS7005 - Variable 'nodes' implicitly has an 'any[]' type.
      ...nodes,
      {
        id: nodeId,
        data: {
          ...node,
          swimlaneId: id,
          //note(vish): we still need this flag because while rendering the graph we don't have access to moduleConfig
          //and this is used to show a different message for scheduling nodes which are under an already existing schedule node.
          scheduleSeenOnce: newScheduleSeenCount > 1,

          //if there is a related node,next_nodes of this node should be ignored and point to the related node's
          //next node instead
          next_nodes: nextNodes,
        },
        isRoot,
      },
    ];
    // }

    if (nextNodes.length > 0) {
      edges = [
        ...edges,
        ...nextNodes.reduce<Array<any>>((acc, nextNode) => {
          if (nextNode.next_node) {
            acc.push({
              id: `edge_${nodeId}_${nextNode.next_node}`,
              from: nodeId,
              to: nextNode.next_node,
              actionable: true,
            });
          }
          return acc;
        }, []),
      ];

      nextNodes.forEach((nextNode) => {
        const {
          nodes: newNodes,
          edges: newEdges,
          groups: newGroups,
          swimlaneRecurNodeSet: newSwimlaneRecurNodeSet,
          swimlaneBatchingNodeSet: newSwimlaneBatchingNodeSet,
        } = traverseNode(
          swimlane,
          nextNode.next_node,
          false,
          newScheduleSeenCount,
        );
        // @ts-expect-error - TS7005 - Variable 'nodes' implicitly has an 'any[]' type.
        nodes = [...nodes, ...newNodes];
        edges = [...edges, ...newEdges];
        groups = [...groups, ...newGroups];
        swimlaneRecurNodeSet = new Set([
          ...swimlaneRecurNodeSet,
          ...newSwimlaneRecurNodeSet,
        ]);
        swimlaneBatchingNodeSet = new Set([
          ...swimlaneBatchingNodeSet,
          ...newSwimlaneBatchingNodeSet,
        ]);
      });
    }
  }
  /*
   * Note(Vish): Flow is facing issues mapping body object correctly in this recursive function.
   * This is happening because body is different for Scheduling node vs Task node.
   * Fixing it will require rewriting node types or making changes in the recursive function, both of which will take significant effort.
   * Supressing for now.
   * */
  // @ts-expect-error - TS7005 - Variable 'nodes' implicitly has an 'any[]' type.
  return {nodes, edges, groups, swimlaneRecurNodeSet, swimlaneBatchingNodeSet};
};

export const normalizeAutomationWorkflow = (
  automationWorkflowState: AutomationWorkflowState,
): Partial<AutomationWorkflowApi> => {
  const {name, entity_type} = automationWorkflowState;
  if (!name || !entity_type) {
    throw new Error('Automation workflow name, and entity type are required');
  }
  return {
    name,
    entity_type,
  };
};

export const checkWorkflowContainsTempNode = (
  swimlanes: Array<Swimlane>,
): boolean => {
  return swimlanes.some((swimlane) => {
    return Object.keys(swimlane.nodes).some(
      (nodeId) => nodeId === TEMP_NODE_IDENTIFIER,
    );
  });
};

export const checkWorkflowContainsForeachNode = (
  swimlanes: Array<Swimlane>,
): boolean => {
  return swimlanes.some((swimlane) => {
    return Object.keys(swimlane.nodes).some(
      (nodeId) => swimlane.nodes[nodeId].body.composite_node_type === 'foreach',
    );
  });
};

export const checkWorkflowInvalidActivation = (
  swimlanes: Array<Swimlane>,
): boolean => {
  return swimlanes.some((swimlane) => {
    // return false if any swimlane has no sms or email node
    const communicationNodeCount = Object.keys(swimlane.nodes).filter(
      (nodeId) => {
        const compositeNodeType =
          swimlane.nodes[nodeId].body.composite_node_type;
        return [
          'sms',
          'email',
          'sms-chatbot',
          'candidate-matching',
          'update-record',
          'create-ats-record',
          'custom-note-writeback',
          'candidate-evaluation',
          'voicebot',
          'voicebot-v2',
          'whatsapp',
          'ai-generator',
        ].includes(compositeNodeType);
      },
    ).length;
    return communicationNodeCount === 0;
  });
};

export const getFormattedBrandsList = (
  brands: Array<Brand> | null | undefined,
  selectedBrandId: string,
  t: (key: string, defaultValue: string) => string,
): PropsWithIconURL[] => {
  const isSelected = (brand: Brand) => {
    return brand.id === selectedBrandId;
  };
  brands?.sort((a: Brand, b: Brand) => {
    const isSelectedA = isSelected(a);
    const isSelectedB = isSelected(b);
    if (isSelectedA !== isSelectedB) {
      return isSelectedA ? -1 : 1;
    }
    if (a.is_primary !== b.is_primary) {
      return a.is_primary ? -1 : 1;
    }
    return 0;
  });
  return (
    brands?.map(({id, display_name, logo, is_primary}) => ({
      title: getBrandDisplayName(display_name, is_primary, t),
      value: id,
      iconUrl: logo || DEFAULT_BEEFREE_LOGO_URL,
    })) ?? []
  );
};

export function createQueryObjectFromNodeInfo(nodeInfo: {
  tid?: string | null | undefined;
  sid?: string | null | undefined;
  swid?: string | null | undefined;
  ae?: string | null | undefined;
  an?: string | null | undefined;
  bpos?: string | null | undefined;
  prvid?: string | null | undefined;
  hfNodeid?: string | null | undefined;
  hfSwid?: string | null | undefined;
  hfType?: string | null | undefined;
  sn?: string | null | undefined;
}): {
  [key: string]: string;
} {
  return [
    'tid',
    'sid',
    'swid',
    'ae',
    'an',
    'bpos',
    'prvid',
    'hfNodeid',
    'hfSwid',
    'hfType',
    'sn',
  ].reduce<Record<string, any>>((acc, field) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ tid?: string | null | undefined; sid?: string | null | undefined; swid?: string | null | undefined; ae?: string | null | undefined; an?: string | null | undefined; bpos?: string | null | undefined; prvid?: string | ... 1 more ... | undefined; hfNodeid?: string | ... 1 more ... | undefined; hfSwid?: string | ... 1 ...'.
    if (nodeInfo[field] != null) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ tid?: string | null | undefined; sid?: string | null | undefined; swid?: string | null | undefined; ae?: string | null | undefined; an?: string | null | undefined; bpos?: string | null | undefined; prvid?: string | ... 1 more ... | undefined; hfNodeid?: string | ... 1 more ... | undefined; hfSwid?: string | ... 1 ...'.
      acc[field] = nodeInfo[field];
    }
    return acc;
  }, {});
}

function findDescendants(key: string, descendants: Record<any, any>) {
  const result = new Set();
  if (descendants.hasOwnProperty(key)) {
    for (const child of descendants[key]) {
      result.add(child);
      for (const descendant of findDescendants(child, descendants)) {
        result.add(descendant);
      }
    }
  }
  return result;
}

// Helper function to recursively find ancestors
function findAncestors(key: string, ancestors: Record<any, any>) {
  const result = new Set();
  if (ancestors.hasOwnProperty(key)) {
    for (const parent of ancestors[key]) {
      result.add(parent);
      for (const ancestor of findAncestors(parent, ancestors)) {
        result.add(ancestor);
      }
    }
  }
  return result;
}

//Note(Vish): This group related logic can be moved to group selector. But that file is absent atm. Refactor later.
export const generateAncestorAndDescendantArray = (
  groups: WorkflowGroup,
): {
  [key: string]: {
    parents: Set<string>;
    children: Set<string>;
  };
} => {
  const ancestors: Record<string, any> = {};
  const descendants: Record<string, any> = {};

  // Construct the ancestor and descendant relationships
  for (const key in groups) {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Group'.
    const parent = groups[key].parentSecurityGroupId;
    if (parent !== null) {
      if (!descendants.hasOwnProperty(parent)) {
        descendants[parent] = new Set();
      }
      descendants[parent].add(key);

      if (!ancestors.hasOwnProperty(key)) {
        ancestors[key] = new Set();
      }
      ancestors[key].add(parent);
    }
  }

  // Generate relations for each individual
  const relations: Record<string, any> = {};
  for (const key in groups) {
    const individual = key;
    const ancestorsSet = findAncestors(individual, ancestors);
    const descendantsSet = findDescendants(individual, descendants);
    relations[individual] = {
      parents: ancestorsSet,
      children: descendantsSet,
    };
  }

  return relations;
};

export const removeSwimlaneTempNodes = (
  swimlane: Swimlane,
  nodeId: string,
  // @ts-expect-error - TS7030 - Not all code paths return a value.
): unknown => {
  if (!nodeId || !swimlane.nodes[nodeId]) {
    return; // Base condition: return if nodeId is falsy or node doesn't exist
  }
  const node = swimlane.nodes[nodeId];
  const nextNodes = node.next_nodes ?? [];

  if (nextNodes.length > 0) {
    const nextNodesWithoutTempNodes = nextNodes.reduce<Array<any>>(
      (acc, nextNode) => {
        // Adjusting the pointers/next nodes
        const nextNodeId = nextNode.next_node;
        if (
          nextNodeId.includes('temp__') ||
          nextNodeId.startsWith(TEMP_END_NODE_IDENTIFIER)
        ) {
          const nextNodeArray = swimlane.nodes[nextNodeId].next_nodes;
          const nextOfNextNodeId =
            nextNodeArray.length > 0 ? nextNodeArray[0].next_node : null;
          if (nextOfNextNodeId) {
            // Also remove tempNode from swimlane nodes
            nextNodeArray.forEach((nextNode) => {
              if (nextNode.next_node.startsWith(TEMP_END_NODE_IDENTIFIER)) {
                delete swimlane.nodes[nextNode.next_node];
              }
            });
            delete swimlane.nodes[nextNodeId];
            return [...acc, {...nextNode, next_node: nextOfNextNodeId}];
          } else {
            // This means that the temp node is end node. Which implies that the current node has a temp transition node, which needs to be removed
            delete swimlane.nodes[nextNodeId];
            return acc;
          }
        }
        return [...acc, nextNode];
      },
      [],
    );

    const newNode = {...node, next_nodes: nextNodesWithoutTempNodes} as const;
    swimlane.nodes[nodeId] = newNode;

    if (nextNodesWithoutTempNodes.length > 0) {
      nextNodesWithoutTempNodes.forEach((nextNode) => {
        removeSwimlaneTempNodes(swimlane, nextNode.next_node);
      });
    }
  }
};

export const getNodeInfo = ({
  data,
  transitionId,
  swimlane,
}: {
  data: CompositeAutomationNode;
  transitionId?: string | null | undefined;
  swimlane?: Swimlane | null | undefined;
}): {
  sid: string;
  tid: string | null | undefined;
  swid: string;
  prvid: string | null | undefined;
} => {
  let prvNodeId = null;
  if (swimlane && swimlane.nodes) {
    prvNodeId = getPreviousNodeId({swimlane, data});
  }
  const effectiveTransitionId = transitionId ? Number(transitionId) : 0;
  const nodeInfo = {
    sid: data.id,
    tid: data.next_nodes[effectiveTransitionId]
      ? data.next_nodes[effectiveTransitionId].next_node
      : null,
    swid: String(data.swimlaneId),
    prvid: prvNodeId ?? null,
  } as const;
  return nodeInfo;
};

export const getTransitionId = (
  swimlanes: Array<Swimlane>,
  swid: string,
  sid: string,
  tid: string,
): number => {
  const nextNodes =
    // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'string' and 'number' have no overlap.
    swimlanes.find(({id}) => id === Number(swid))?.nodes[sid].next_nodes ?? [];
  //Note(vish): we are send the array index as transition id since we don't have any sort of serialization atm.
  const transitionId = nextNodes.findIndex(({next_node}) => next_node === tid);
  return transitionId >= 0 ? transitionId : 0;
};

export const getAllChildNodes = (
  recurNode: CanvasGraphNode,
  nodes: Array<CanvasGraphNode>,
): Array<string> => {
  const relatedNodes: Array<string> = [];
  function process(rec: undefined | CanvasGraphNode) {
    relatedNodes.push(rec?.id ?? '');
    if (rec && rec.data.next_nodes.length > 0) {
      rec.data.next_nodes.forEach((nextNode) => {
        const recNode = nodes.find((node) => node.id === nextNode.next_node);
        process(recNode);
      });
    }
  }
  process(recurNode);
  return relatedNodes;
};

const getFrequencyLimit = (
  frequencyLimit: FrequencyLimit,
): string | null | undefined => {
  if (!frequencyLimit) {
    return null;
  }
  const {count, duration} = frequencyLimit;
  if (count < 0) {
    return FREQUENCY_LIMIT.NO_RESTRICTION;
  }
  if (duration.type === 'FOREVER') {
    return FREQUENCY_LIMIT.RESTRICTION;
  }
  return FREQUENCY_LIMIT.RESTRICT_REPEAT;
};

const getFrequencyType = (frequencyLimit: FrequencyLimit): string => {
  if (!frequencyLimit) {
    return '1_DAY';
  }
  const {duration} = frequencyLimit;
  if (duration.type === 'FOREVER') {
    return '1_DAY';
  }
  return Object.keys(FREQUENCY_DURATION_OPTIONS).reduce((acc, key) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly '1_DAY': { readonly label: "Once a day"; readonly duration: { readonly type: "DAY"; readonly value: 1; readonly count: 1; }; }; readonly '2_DAY': { readonly label: "Twice a day"; readonly duration: { readonly type: "DAY"; readonly value: 1; readonly count: 2; }; }; ... 9 more ...; readonly '365_DAYS': { ....'.
    const {type, value, count} = FREQUENCY_DURATION_OPTIONS[key].duration;
    if (
      frequencyLimit.count === count &&
      frequencyLimit.duration.type === type &&
      frequencyLimit.duration.value === value
    ) {
      return key;
    }
    return acc;
  }, '1_DAY');
};

export const isSingleDayFrequency = (
  frequencyType: string,
  t: (arg1: string, arg2: string) => string,
): boolean => {
  const frequencyDurationOptions = getFrequencyDurationOptions(t);
  return (
    frequencyDurationOptions[frequencyType].duration.count > 1 ||
    frequencyType === '1_DAY'
  );
};

const getRestrictRepeatSummary = (
  frequencyType: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  const frequencyDurationOptions = getFrequencyDurationOptions(t);
  const isSingleDayFreq = isSingleDayFrequency(frequencyType, t);
  const label = frequencyDurationOptions[frequencyType].label;
  return `${t('restrict_and_repeat', 'restrict and repeat')} ${
    !isSingleDayFreq ? t('only_after', 'only after') : ''
  } ${label.toLowerCase()}`;
};

export const generateFrequencySummary = (
  frequencyLimit: string | null | undefined,
  frequencyType: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  if (!frequencyLimit) {
    return '';
  }
  let summaryText = '';
  if (frequencyLimit === FREQUENCY_LIMIT.NO_RESTRICTION) {
    summaryText = t(
      'don\'t_restrict_and_send_multiple_times',
      'don\'t restrict and send multiple times',
    );
  } else if (frequencyLimit === FREQUENCY_LIMIT.RESTRICTION) {
    summaryText = t(
      'restrict_sending_multiple_times',
      'restrict sending multiple times',
    );
  } else {
    summaryText = getRestrictRepeatSummary(frequencyType, t);
  }
  return `${t('Summary', 'Summary')}: ${t(
    'If_communication_is_getting_repeated',
    'If communication is getting repeated',
  )} ${summaryText}`;
};

export const parseFrequencyLimit = (
  frequencyLimit: string,
  frequencyType: string,
  t: (arg1: string, arg2: string) => string,
): FrequencyLimit => {
  const freqLimit = {
    count: -1,
    duration: {type: 'FOREVER', value: 0},
  } as const;
  if (frequencyLimit === FREQUENCY_LIMIT.RESTRICTION) {
    // @ts-expect-error - TS2540 - Cannot assign to 'count' because it is a read-only property.
    freqLimit.count = 1;
  } else if (frequencyLimit === FREQUENCY_LIMIT.RESTRICT_REPEAT) {
    const {duration} = getFrequencyDurationOptions(t)[frequencyType];
    // @ts-expect-error - TS2540 - Cannot assign to 'count' because it is a read-only property.
    freqLimit.count = duration.count;
    // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
    freqLimit.duration.type = duration.type;
    // @ts-expect-error - TS2540 - Cannot assign to 'value' because it is a read-only property.
    freqLimit.duration.value = duration.value;
  }
  return freqLimit;
};

export const isTempNode = (nodeId: string): boolean => {
  return nodeId === TEMP_NODE_IDENTIFIER;
};

// When workflow is active , route the node click to track node activity else route to node details
export const getRouteForNode = (
  status: WorkflowStatus,
): 'track' | 'details' => {
  if (status === 'ACTIVE') {
    return 'track';
  }
  return 'details';
};

export function findPreviousNodeId(
  nodeId: string,
  nodes: {
    [key: string]: CompositeAutomationNodeApi;
  },
): string | null | undefined {
  for (const key in nodes) {
    const node = nodes[key]; // Flow knows node is of type Node
    if (node.next_nodes) {
      for (const nextNode of node.next_nodes) {
        if (nextNode.next_node === nodeId) {
          return key; // Return the key of the node pointing to the given nodeId
        }
      }
    }
  }
  return null;
}

export const getPreviousNodeId = ({
  swimlane,
  data,
}: {
  swimlane: Swimlane | null | undefined;
  data: CompositeAutomationNode;
}): string | null | undefined => {
  const effectiveNodeId = data.id;
  if (swimlane && swimlane.nodes) {
    return findPreviousNodeId(effectiveNodeId, swimlane.nodes);
  }
  return null;
};

export const getConnectedNodes = (
  swimlane: Swimlane,
  startNodeId: string,
  endNodeId: string,
): Array<string> => {
  const nodes = swimlane.nodes;

  // @ts-expect-error - TS7023 - 'getConnectedNodesHelper' implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions.
  function getConnectedNodesHelper(currentNodeId: string, path: Array<string>) {
    path.push(currentNodeId);
    if (currentNodeId === endNodeId) {
      return path;
    }
    // Check if the current node exists and has next_nodes
    const currentNode = nodes[currentNodeId];
    if (!currentNode || !currentNode.next_nodes) {
      path.pop();
      return null;
    }
    for (const next of currentNode.next_nodes) {
      // @ts-expect-error - TS7022 - 'result' implicitly has type 'any' because it does not have a type annotation and is referenced directly or indirectly in its own initializer.
      const result = getConnectedNodesHelper(next.next_node, path);
      if (result) {
        return result;
      }
    }
    // Backtrack if no path is found
    path.pop();
    return null;
  }
  return getConnectedNodesHelper(startNodeId, []) || [];
};

export const findAllNodesReachableFromNode = (
  swimlane: Swimlane,
  startNodeId: string,
): Array<string> => {
  const nodes = swimlane.nodes;
  const visited = new Set();

  function traverse(nodeId: string) {
    if (visited.has(nodeId)) {
      return;
    }
    visited.add(nodeId);

    const node = nodes[nodeId];
    if (!node || !node.next_nodes) {
      return;
    }

    for (const next of node.next_nodes) {
      traverse(next.next_node);
    }
  }

  traverse(startNodeId);
  // @ts-expect-error - TS2322 - Type 'unknown[]' is not assignable to type 'string[]'.
  return Array.from(visited);
};

export const getHfNodeData = (
  swid: string,
  nodeId: string,
  swimlanes: Array<Swimlane>,
): CompositeAutomationNodeApi | null | undefined => {
  // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'number' and 'string' have no overlap.
  const swimlane = swimlanes.find(({id}) => parseInt(swid, 10) === id);
  const currentNode = swimlane && swimlane.nodes[nodeId];
  return currentNode;
};

export const getHFNodeTitle = (
  nodeData: CompositeAutomationNodeApi,
): string => {
  const {display_name, node_name} = nodeData.body ?? {};
  return `Deleting ${node_name ?? ''} ${display_name ?? ''}`;
};

export const getHFModeExceptionsForTransition = (
  transition: ReceivedFilters,
): Array<string> | null | undefined => {
  let hfmodeExceptions = null;
  if (transition.delete_node_usage) {
    hfmodeExceptions = Object.keys(transition.delete_node_usage).filter(
      (key) =>
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsage'.
        typeof transition.delete_node_usage?.[key] === 'boolean' &&
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsage'.
        transition.delete_node_usage[key],
    );
  }
  return hfmodeExceptions;
};

export const getHFModeExceptionsForTaskNode = (
  hfDeleteInfo: Array<DeleteNodeUsageWithId>,
): Array<string> => {
  if (hfDeleteInfo.length === 0) {
    return [];
  }
  const exceptionsSet = new Set<string>();
  hfDeleteInfo.forEach((nodeDeleteInfo) => {
    for (const key in nodeDeleteInfo) {
      if (
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsageWithId'.
        typeof nodeDeleteInfo[key] === 'boolean' &&
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsageWithId'.
        nodeDeleteInfo[key] === true
      ) {
        exceptionsSet.add(key);
      }
    }
  });
  return Array.from(exceptionsSet);
};

export const getHFModeAffectedVariablesForTaskNode = (
  hfDeleteInfo: Array<DeleteNodeUsageWithId>,
): Array<string> => {
  if (hfDeleteInfo.length === 0) {
    return [];
  }
  // @ts-expect-error - TS7034 - Variable 'variablesGroup' implicitly has type 'any[]' in some locations where its type cannot be determined.
  let variablesGroup = [];
  hfDeleteInfo.forEach((nodeDeleteInfo) => {
    for (const key in nodeDeleteInfo) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsageWithId'.
      if (Array.isArray(nodeDeleteInfo[key])) {
        // @ts-expect-error - TS7005 - Variable 'variablesGroup' implicitly has an 'any[]' type. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsageWithId'.
        variablesGroup = [...variablesGroup, ...nodeDeleteInfo[key]];
      }
    }
  });
  // @ts-expect-error - TS7005 - Variable 'variablesGroup' implicitly has an 'any[]' type.
  return [...new Set(variablesGroup)];
};

export const getHfNodeMap = (
  swid: string,
  swimlanes: Array<Swimlane>,
): Map<string, Array<DeleteNodeUsageWithId>> => {
  const nodeMap = new Map();
  // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'number' and 'string' have no overlap.
  const swimlane = swimlanes.find(({id}) => parseInt(swid, 10) === id);
  if (swimlane) {
    for (const nodeId in swimlane.nodes) {
      // const nodeType = swimlane.nodes[nodeId].body?.node_type;
      const transitionUsageArray = (
        swimlane.nodes[nodeId].next_nodes ?? []
      ).reduce<Array<any>>((acc, curr, index) => {
        if (curr.delete_node_usage) {
          acc.push({
            id: String(index),
            ...curr.delete_node_usage,
          });
        }
        return acc;
      }, []);
      const nodeBodyUsage = swimlane.nodes[nodeId].delete_node_usage ?? null;
      nodeMap.set(
        nodeId,
        nodeBodyUsage
          ? [nodeBodyUsage, ...transitionUsageArray]
          : transitionUsageArray,
      );
    }
  }
  return nodeMap;
};

export type AutomationWorkflowContextType = {
  automationModulesRegistryLoading: boolean;
  deleteNode: (nodeId: string) => unknown;
  createNode: (nodeType: string, node: GenericObject) => Promise<GenericObject>;
  updateNode: (
    nodeType: string,
    nodeId: string,
    node: GenericObject,
    confirmationModalMeta?: {
      confirmationNodeBody: React.ReactNode;
    },
  ) => Promise<GenericObject>;
  addNodeTask: (nodeId: string, task: GenericObject) => Promise<GenericObject>;
  allowBlackoutInScheduling: boolean;
  getChatbotName: (flowId: number) => string;
  getSurveyName: (surveySchemaId: number) => string;
  getMeetingTemplateName: (templateId: string) => string;
  workflowId: string;
  triggerEntityType: string;
  anchorEntityType: string;
  moduleProperties: ModuleProperties | null | undefined;
  refetchStaticSchema: () => unknown;
  refetchDynamicSchema: () => unknown;
  urlGenerator: UrlGenerator;
  // getNoteWBMeta: () => GenericObject,
  hfNodeId: string | null | undefined;
  hfNodeMap: Map<string, Array<DeleteNodeUsageWithId>>;
  getTaskNodeExceptions: (arg1?: string | null | undefined) => Array<string>;
  getTaskNodeAffectedVariables: (arg1: string) => Array<string>;
  isScheduleConfigLoading: boolean;
  closePanel: () => unknown;
  disablePreview: boolean;
  isWorkflowDataLoading: boolean;
  activeWorkflowEditStatusForCurrentNode: AutomationWorkflowEditStatus;
  isEditDisallowed: boolean;
};

// @ts-expect-error - TS2322 - Type 'Context<null>' is not assignable to type 'Context<AutomationWorkflowContextType | null | undefined>'.
export const AutomationWorkflowContext: React.Context<
  AutomationWorkflowContextType | null | undefined
> = React.createContext(null);

export const useAutomationWorkflowContext =
  (): AutomationWorkflowContextType => {
    const context = React.useContext(AutomationWorkflowContext);
    if (!context) {
      throw new Error(
        'AutomationWorkflowContext hook can only be used inside AutomationWorkflowContext.Provider',
      );
    }
    return context;
  };

export function useSwimlaneNode(
  swid: string,
  nodeId: string,
): CompositeAutomationNodeApi | null | undefined {
  return useSelector((state) => selectSwimlaneNode(state, swid, nodeId));
}

/** NOTE:(diwakersurya) this hook exists because we need the internal node ids instead of
 * composite node ids in abt events and analytics calls. If they start consuming
 * composite node ids, we can remove this hook. Don't use this hook for anything else.
 */
export function useInternalNodeId(
  swid: string,
): (nodeId: string) => string | null | undefined {
  const swimlane = useSelector((state) => selectSwimlane(state, swid));
  return function getInternalNodeIdFromCompositeNodeId(nodeId: string) {
    if (!swimlane) {
      return null;
    }
    const node = swimlane.nodes[nodeId];
    if (!node) {
      return null;
    }
    return node.body.internal_node_id;
  };
}

/** NOTE:(diwakersurya) this hook only exists because we need the composite node Id to show the node name in abt rules.
 * If they start consuming composite node ids, we can remove this hook. Don't use this hook for anything else.
 */
export function useCompositeNodeId(
  swid: string,
): (internalNodeId: string) => string | null | undefined {
  const swimlane = useSelector((state) => selectSwimlane(state, swid));
  return function getCompositeNodeIdFromInternalNodeId(internalNodeId: string) {
    if (!swimlane) {
      return null;
    }
    // loop through the swimlane nodes and return the node id that has the internal node id
    for (const nodeId in swimlane.nodes) {
      if (swimlane.nodes[nodeId].body.internal_node_id === internalNodeId) {
        return nodeId;
      }
    }
    return null;
  };
}

export function usePreviousNodeId(
  nodeId: string,
  swid: string,
): string | null | undefined {
  const swimlane = useSelector((state) => selectSwimlane(state, swid));

  if (swimlane && swimlane.nodes && nodeId != null) {
    return findPreviousNodeId(nodeId, swimlane.nodes);
  }
  return null;
}
export const getModuleIconName = (
  totalChatbots: number,
  totalSurveys: number,
): string | null | undefined => {
  if (totalChatbots > 0 && totalSurveys > 0) {
    return 'puzzle';
  }
  if (totalChatbots > 0) {
    return 'robot';
  }
  if (totalSurveys > 0) {
    return 'poll';
  }
  return null;
};

export const isEditNotAllowedForNode = (
  status: AutomationWorkflowEditStatus,
): boolean => {
  return status === 'nonEditable' || status === 'noPermission';
};

export const getExtraIcons = (
  {
    chatbots,
    surveys,
    schedules,
  }: {
    chatbots: Array<string>;
    surveys: Array<string>;
    schedules: Array<string>;
  },
  t: (arg1: string, arg2: string) => string,
): Array<{
  labels: Array<string>;
  iconName: string;
  type?: 'regular' | 'solid';
  moduleType: 'chatbot' | 'survey' | 'schedule';
}> => {
  const extraIcons: Array<{
    labels: Array<string>;
    iconName: string;
    type?: 'regular' | 'solid';
    moduleType: 'chatbot' | 'survey' | 'schedule';
  }> = [];
  if (chatbots.length > 0) {
    extraIcons.push({
      labels: chatbots,
      iconName: 'robot',
      type: 'regular',
      moduleType: 'chatbot',
    });
  }
  if (surveys.length > 0) {
    extraIcons.push({
      labels: surveys,
      iconName: 'poll',
      type: 'regular',
      moduleType: 'survey',
    });
  }
  if (schedules.length > 0) {
    extraIcons.push({
      labels: schedules,
      iconName: 'calendar',
      type: 'regular',
      moduleType: 'schedule',
    });
  }
  return extraIcons;
};

export const getSwimlaneSearchOptions = (
  swimlanes: Array<Swimlane>,
): Array<MenuOption> => {
  const allNodes: Array<CompositeAutomationNodeApi> = swimlanes.reduce(
    (acc, swimlane) => {
      const nodes = swimlane.nodes ?? {};
      return [...acc, ...Object.values(nodes)];
    },
    [] as Array<CompositeAutomationNodeApi>,
  );

  if (allNodes.length === 0) {
    return [];
  }

  return allNodes.reduce((acc, node) => {
    if (node.body.composite_node_type === 'trigger') {
      acc.push({
        key: node.body.composite_id,
        label: `Trigger`,
      });
    } else if (
      node.body.composite_node_type === 'path' ||
      node.body.composite_node_type === 'split'
    ) {
      const nodeName = node.body.node_name;
      const compositeNodeId = node.body.composite_id;
      node.next_nodes.forEach((nextNode) => {
        const transitionId = `${compositeNodeId};${nextNode.transition_id}`;
        const transitionName = getCompleteName(
          nodeName,
          nextNode.display_name ?? '',
        );
        acc.push({
          key: transitionId,
          label: transitionName,
        });
      });
    } else {
      if (!!node.body.display_name && !!node.body.node_name) {
        acc.push({
          key: node.body.composite_id,
          label: `${node.body.node_name} ${node.body.display_name}`,
        });
      }
    }
    return acc;
  }, [] as Array<MenuOption>);
};

export const getTitleText = (
  moduleType: 'chatbot' | 'survey' | 'schedule',
  moduleCount: number,
  t: (arg1: string, arg2: string) => string,
): string => {
  if (moduleType === 'chatbot') {
    return `${moduleCount} ${formatPlural(
      moduleCount,
      t('Chatbot', 'Chatbot'),
      t('Chatbots', 'Chatbots'),
    )} :`;
  }
  if (moduleType === 'survey') {
    return `${moduleCount} ${formatPlural(
      moduleCount,
      t('Survey', 'Survey'),
      t('Surveys', 'Surveys'),
    )} :`;
  }
  return `${moduleCount} ${formatPlural(
    moduleCount,
    t('Scheduler_Link', 'Scheduler Link'),
    t('Scheduler_Links', 'Scheduler Links'),
  )} :`;
};

export const getSingleModuleLabel = (
  moduleType: 'chatbot' | 'survey' | 'schedule',
  t: (arg1: string, arg2: string) => string,
): string => {
  if (moduleType === 'chatbot') {
    return t('Chatbot', 'Chatbot');
  }
  if (moduleType === 'survey') {
    return t('Survey', 'Survey');
  }
  return t('Scheduler_Link', 'Scheduler Link');
};
